import config from 'src/config'
import templateConfig from 'template/config'

export interface getOptions {
  url: string
  signal?: AbortSignal
  local?: boolean
  headers?: Record<string, string>
}

// These POST endpoints only read catalog data. Never retry arbitrary POSTs:
// a lost response can follow a successful order, payment or contact submission.
const isRetryableRead = (method: 'GET' | 'POST', url: string) =>
  method === 'GET' || url === 'products' || /^product\/[1-9]\d*$/.test(url)

const transientTransportCodes = new Set([
  'UND_ERR_SOCKET', 'ECONNRESET', 'ECONNREFUSED', 'EPIPE', 'ETIMEDOUT',
  'EAI_AGAIN', 'UND_ERR_CONNECT_TIMEOUT', 'UND_ERR_HEADERS_TIMEOUT', 'UND_ERR_BODY_TIMEOUT',
])

const errorDetails = (error: unknown) => {
  const value = error as { name?: unknown; cause?: { code?: unknown }; code?: unknown } | null
  const code = value?.cause?.code ?? value?.code
  return {
    aborted: value?.name === 'AbortError',
    transportCode: typeof code === 'string' && transientTransportCodes.has(code) ? code : undefined,
    label: value?.name === 'SyntaxError' ? 'INVALID_JSON' : 'REQUEST_ERROR',
  }
}

// Fixed route labels only: no URL query, product IDs, tokens, request bodies,
// exception messages or other customer data are written to the runtime log.
const endpointLabel = (url: string) => {
  const path = url.split(/[?#]/, 1)[0]
  if ([
    'products', 'products/guarantees', 'products/listing-group',
    'product/previews/commerce', 'brands', 'countries', 'magazines',
    'kontakt', 'withdrawal', 'order/new', 'order/getByToken',
    'paypal/createPayment', 'paypal/capturePayment', 'paypal/createClientToken',
  ].includes(path)) return path
  if (/^product\/[1-9]\d*$/.test(path)) return 'product/:id'
  if (/^product\/[1-9]\d*\/recommendations$/.test(path)) return 'product/:id/recommendations'
  if (/^product\/[1-9]\d*\/cross-sell$/.test(path)) return 'product/:id/cross-sell'
  return 'other'
}

const waitForRetry = (signal?: AbortSignal) => new Promise<boolean>(resolve => {
  if (signal?.aborted) { resolve(false); return }
  const finish = (ready: boolean) => {
    clearTimeout(timer)
    signal?.removeEventListener('abort', aborted)
    resolve(ready)
  }
  const aborted = () => finish(false)
  const timer = setTimeout(() => finish(true), 150)
  signal?.addEventListener('abort', aborted, { once: true })
})

const requestJson = async (
  method: 'GET' | 'POST',
  { url, signal, local, headers = {} }: getOptions,
  body?: string,
): Promise<unknown> => {
  for (let attempt = 0; attempt < 2; attempt++) {
    if (signal?.aborted) return null
    let response: Response | undefined
    try {
      response = await fetch(`${config.api_server[local ? 'local' : 'online']}/${url}`, {
        method,
        ...(body !== undefined ? { body } : {}),
        headers: { 'Content-Type': 'application/json', ...headers },
        signal,
      })
      if (!response.ok && isRetryableRead(method, url)) {
        // The product API explicitly returns 404 + JSON false for missing,
        // offline or unassigned products. Preserve that expected notFound value.
        if (method === 'POST' && /^product\/[1-9]\d*$/.test(url) && response.status === 404) {
          const notFound = await response.json()
          if (notFound === false) return false
        }
        console.error(`API read failed: ${method} ${endpointLabel(url)} (HTTP_${response.status}); no retry`)
        // Do not turn an upstream error payload into valid ISR props. Mutating
        // POST callers still receive their existing JSON error payload below.
        if (!response.bodyUsed) await response.body?.cancel()
        return null
      }
      return await response.json()
    } catch (error) {
      const details = errorDetails(error)
      if (signal?.aborted || details.aborted) return null
      const retry = attempt === 0 && isRetryableRead(method, url)
        && Boolean(details.transportCode) && (!response || response.ok)
      const context = `${method} ${endpointLabel(url)} (${details.transportCode || details.label})`
      if (retry) {
        console.warn(`API request transport failure: ${context}; retry 1/1`)
        if (await waitForRetry(signal)) continue
      } else {
        console.error(`API request failed: ${context}; no further retry`)
      }
      return null
    }
  }
  return null
}

/** Send a GET request; transient transport failures get one bounded retry. */
export const get = (options: getOptions): Promise<unknown> => requestJson('GET', options)

export interface postOptions extends getOptions {
  body: unknown
}

/** Preserve response/customizer semantics; only explicit catalog reads retry. */
export const post = async ({ body, ...options }: postOptions): Promise<unknown> => {
  const requestBody = options.url === 'products' && body && typeof body === 'object' && typeof (body as { suche?: unknown }).suche === 'string'
    ? { ...body, searchRulesVersion: 1 } : body
  const res = await requestJson('POST', options, JSON.stringify(requestBody))
  const customizeProductPages = (templateConfig as {
    customizeProductPages?: (response: unknown, request: unknown) => unknown
  }).customizeProductPages
  return options.url === 'products' && customizeProductPages
    ? customizeProductPages(res, body)
    : res
}
